import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, ThumbsUp, ThumbsDown, Send, User, Bot, Sparkles } from 'lucide-react';

type Message = {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
};

type AIFeedbackSystemProps = {
  careerPath: string;
  onClose?: () => void;
  fullScreen?: boolean;
};

const AIFeedbackSystem: React.FC<AIFeedbackSystemProps> = ({ 
  careerPath = 'product-manager', 
  onClose, 
  fullScreen = false 
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Mensaje inicial basado en la carrera seleccionada
    const initialMessages = getInitialMessages(careerPath);
    setMessages(initialMessages);
  }, [careerPath]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getInitialMessages = (path: string): Message[] => {
    const commonMessages = [
      {
        id: '1',
        content: `Hola, soy tu mentor de ${formatCareerPath(path)}. Estoy aquí para ayudarte a desarrollar tus habilidades profesionales y responder tus preguntas. ¿En qué puedo ayudarte hoy?`,
        sender: 'ai',
        timestamp: new Date(),
      }
    ];

    return commonMessages;
  };

  const formatCareerPath = (path: string): string => {
    const paths: Record<string, string> = {
      'product-manager': 'Product Management',
      'developer': 'Desarrollo de Software',
      'entrepreneur': 'Emprendimiento',
      'tech-entrepreneur': 'Emprendimiento Tecnológico',
      'consultant': 'Consultoría',
      'project-manager': 'Gestión de Proyectos'
    };
    return paths[path] || path;
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simular respuesta de la IA
    setTimeout(() => {
      const aiResponse = generateAIResponse(input, careerPath);
      const aiMessage: Message = {
        id: Date.now().toString(),
        content: aiResponse,
        sender: 'ai',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const generateAIResponse = (userInput: string, path: string): string => {
    // Respuestas específicas basadas en palabras clave y la carrera
    const lowercaseInput = userInput.toLowerCase();
    
    // Respuestas para Product Manager
    if (path === 'product-manager') {
      if (lowercaseInput.includes('roadmap') || lowercaseInput.includes('hoja de ruta')) {
        return "Un buen roadmap de producto debe equilibrar el valor para el usuario, el valor para el negocio y el esfuerzo técnico. Te recomiendo utilizar la metodología RICE (Reach, Impact, Confidence, Effort) para priorizar funcionalidades. ¿Necesitas ayuda para implementar esta metodología en tu proyecto actual?";
      }
      if (lowercaseInput.includes('priorizar') || lowercaseInput.includes('priorización')) {
        return "La priorización es clave en Product Management. Además de RICE, considera también el framework MoSCoW (Must have, Should have, Could have, Won't have) para categorizar funcionalidades. Recuerda siempre validar tus prioridades con datos de usuarios y alinearlas con los objetivos estratégicos del negocio.";
      }
      if (lowercaseInput.includes('usuario') || lowercaseInput.includes('user research')) {
        return "La investigación de usuarios es fundamental. Te recomiendo combinar métodos cuantitativos (análisis de datos, encuestas) con métodos cualitativos (entrevistas, pruebas de usabilidad). ¿Has establecido un proceso regular de feedback de usuarios para tu producto?";
      }
    }
    
    // Respuestas para Developer
    if (path === 'developer') {
      if (lowercaseInput.includes('arquitectura') || lowercaseInput.includes('architecture')) {
        return "Para diseñar una buena arquitectura de software, considera principios como SOLID, separación de responsabilidades y modularidad. Evalúa patrones como microservicios vs monolito según las necesidades específicas de tu proyecto. ¿Estás trabajando en alguna arquitectura específica actualmente?";
      }
      if (lowercaseInput.includes('rendimiento') || lowercaseInput.includes('performance')) {
        return "Para mejorar el rendimiento, enfócate primero en identificar cuellos de botella mediante profiling. Optimiza consultas a bases de datos, implementa caching estratégico y considera la carga diferida de recursos. Recuerda que las optimizaciones prematuras pueden complicar el código innecesariamente.";
      }
      if (lowercaseInput.includes('testing') || lowercaseInput.includes('pruebas')) {
        return "Un enfoque equilibrado de testing incluiría pruebas unitarias para lógica de negocio, pruebas de integración para interacciones entre componentes y pruebas end-to-end para flujos críticos. Considera implementar TDD (Test-Driven Development) para funcionalidades complejas.";
      }
    }
    
    // Respuestas para Entrepreneur
    if (path === 'entrepreneur' || path === 'tech-entrepreneur') {
      if (lowercaseInput.includes('validar') || lowercaseInput.includes('validación')) {
        return "Para validar tu idea de negocio, comienza con entrevistas a clientes potenciales para confirmar el problema. Luego, crea un MVP (Producto Mínimo Viable) para validar la solución con el menor costo posible. Establece métricas claras de éxito antes de comenzar la validación.";
      }
      if (lowercaseInput.includes('financiamiento') || lowercaseInput.includes('inversión')) {
        return "Antes de buscar financiamiento externo, considera opciones como bootstrapping o financiamiento con clientes. Si decides buscar inversores, prepara un pitch deck sólido, conoce tus métricas clave y ten clara tu valoración. Recuerda que diferentes etapas requieren diferentes tipos de inversores.";
      }
      if (lowercaseInput.includes('marketing') || lowercaseInput.includes('adquisición')) {
        return "Para la adquisición de clientes con presupuesto limitado, enfócate en canales orgánicos como SEO, marketing de contenidos y redes sociales. Identifica dónde está tu audiencia y concéntrate en 2-3 canales principales. Mide el CAC (Costo de Adquisición de Clientes) y optimiza constantemente.";
      }
    }
    
    // Respuestas generales para cualquier carrera
    if (lowercaseInput.includes('consejo') || lowercaseInput.includes('recomendación')) {
      return "Mi principal recomendación es que combines el aprendizaje teórico con la práctica constante. Busca proyectos reales donde puedas aplicar lo aprendido, solicita feedback regularmente y mantente actualizado con las tendencias de la industria. ¿Hay alguna área específica en la que te gustaría profundizar?";
    }
    if (lowercaseInput.includes('error') || lowercaseInput.includes('problema')) {
      return "Cuando enfrentas errores o problemas, adopta un enfoque sistemático: 1) Define claramente el problema, 2) Identifica posibles causas, 3) Prueba soluciones de forma aislada, 4) Documenta lo aprendido. Recuerda que los errores son oportunidades valiosas de aprendizaje en cualquier carrera profesional.";
    }
    if (lowercaseInput.includes('aprender') || lowercaseInput.includes('estudiar')) {
      return "Para un aprendizaje efectivo, recomiendo el método 70-20-10: 70% aprendizaje práctico en proyectos reales, 20% aprendizaje social (mentorías, comunidades), y 10% educación formal (cursos, libros). ¿Qué habilidades específicas estás buscando desarrollar?";
    }
    
    // Respuesta por defecto si no hay coincidencias específicas
    const defaultResponses = [
      "Esa es una excelente pregunta. Basado en mi experiencia, te recomendaría enfocarte primero en entender profundamente el problema antes de buscar soluciones. ¿Puedes compartir más detalles sobre tu situación específica?",
      "Para avanzar en esta área, considera dedicar tiempo a investigar las mejores prácticas de la industria y adaptar lo que funcione para tu caso específico. ¿Has explorado algún recurso o metodología particular hasta ahora?",
      "Un enfoque efectivo sería dividir este desafío en partes más pequeñas y abordables, priorizando las que generan mayor impacto. ¿Te gustaría que exploremos juntos cómo estructurar este proceso?",
      "Te recomendaría complementar tu aprendizaje práctico con algunos recursos teóricos. La combinación de teoría y práctica suele dar los mejores resultados. ¿Hay algún área específica donde necesites más orientación?"
    ];
    
    return defaultResponses[Math.floor(Math.random() * defaultResponses.length)];
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg flex flex-col ${fullScreen ? 'fixed inset-0 z-50' : 'h-[500px]'}`}>
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-lg flex justify-between items-center">
        <div className="flex items-center">
          <Bot className="h-6 w-6 mr-2" />
          <h3 className="font-medium">Mentor IA - {formatCareerPath(careerPath)}</h3>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
      <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.sender === 'user'
                  ? 'bg-accent text-white'
                  : 'bg-white border border-gray-200 text-gray-800'
              }`}
            >
              <div className="flex items-start">
                {message.sender === 'ai' && (
                  <div className="flex-shrink-0 mr-2">
                    <Sparkles className="h-5 w-5 text-accent" />
                  </div>
                )}
                <div>
                  <p className="text-sm">{message.content}</p>
                  <p className="text-xs mt-1 opacity-70">
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {message.sender === 'user' && (
                  <div className="flex-shrink-0 ml-2">
                    <User className="h-5 w-5 text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start mb-4">
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-gray-800">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={handleSendMessage} className="p-4 border-t flex">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu mensaje..."
          className="flex-1 border border-gray-300 rounded-l-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
        />
        <button
          type="submit"
          className="bg-accent text-white px-4 py-2 rounded-r-lg hover:bg-accent/90 transition-colors"
        >
          <Send className="h-5 w-5" />
        </button>
      </form>
      
      <div className="p-3 bg-gray-50 border-t text-xs text-gray-500 flex justify-between items-center">
        <div>
          ¿Fue útil esta conversación?
          <button className="ml-2 text-gray-400 hover:text-green-500">
            <ThumbsUp className="h-4 w-4" />
          </button>
          <button className="ml-1 text-gray-400 hover:text-red-500">
            <ThumbsDown className="h-4 w-4" />
          </button>
        </div>
        <div>
          Powered by RealMentor AI
        </div>
      </div>
    </div>
  );
};

export default AIFeedbackSystem